import { useParams } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Stack, Dialog, DialogContent } from "@mui/material";
import { useState, useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import { QRCodeCanvas } from "qrcode.react";
import { useGetTaasCard } from "../../../../../services/api/card.api";
import { useCurrentTalentProfile } from "../../../../../hooks";
import SlickSlider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const Slider = SlickSlider as unknown as React.FC<any>;

const CreateFlyoverPage = () => {
  const { id } = useParams<{ id: string }>();
  const { currentProfile } = useCurrentTalentProfile();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [dateTime, setDateTime] = useState("");
  const [duration, setDuration] = useState("");
  const [suitType, setSuitType] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [gallery, setGallery] = useState<string[]>([]);
  const [members, setMembers] = useState<number | null>(null);
  const [talentName, setTalentName] = useState("");
  const [bgColor] = useState("linear-gradient(135deg, #fdfcfb 0%, #e2d1c3 100%)");
  const [previewUrl, setPreviewUrl] = useState("");
  const [hasCreated, setHasCreated] = useState(false);
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [fullscreenImg, setFullscreenImg] = useState<string | null>(null);

  const numericId = id ? parseInt(id, 10) : undefined;

  const {
    data: cardData,
    isLoading,
    error,
  } = useGetTaasCard({
    key: ["taas-card", numericId],
    params: { id: numericId as number },
    options: { enabled: !!numericId },
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setTimeoutReached(true);
    }, 3000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (cardData?.data) {
      const card = cardData.data;
      setTitle(card.title);
      setDescription(card.description || "");
      setPrice(card.price ? `$${card.price}` : "");
      setDateTime(card.start_date_time || "");
      setDuration(card.start_time || "");
      setSuitType(card.suit || "");
      const resolvedImage = typeof card.primary_thumbnail === "string" ? card.primary_thumbnail : "";
      setImageUrl(resolvedImage);
      const galleryImages = (card.gallery_images || []).filter((img: any) => typeof img === "string");
      setGallery(galleryImages.slice(0, 4));
      setMembers(card.max_number_of_people || null);
      setTalentName(card?.talent.username);
      setPreviewUrl(`https://app.taasnet.com/cards/${id}`);
    }
  }, [cardData, id]);

  useEffect(() => {
    if (title && !hasCreated) {
      enqueueSnackbar("Flyover created successfully!", { variant: "success" });
      setHasCreated(true);
    }
  }, [title, hasCreated]);

  if ((isLoading && !timeoutReached) || (!cardData?.data && !timeoutReached)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !cardData?.data) {
    console.error("❌ Failed to fetch card:", error);
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Paper
          sx={{
            p: 4,
            maxWidth: 500,
            mx: "auto",
            borderRadius: 3,
            backgroundColor: "#fff3f3",
            border: "1px solid #ffccd5",
            boxShadow: 3,
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            Unable to Load Flyover
          </Typography>
          <Typography variant="body1" color="text.secondary">
            We're sorry, but we couldn't fetch the card details. This might be due to a network issue or an invalid link.
          </Typography>
          <Typography variant="body2" mt={2} fontStyle="italic">
            Please try again later or contact support if the issue persists.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
  };

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", py: 6, px: 3 }}>
      <Typography
        variant="h4"
        fontWeight={700}
        textAlign="center"
        mb={4}
        sx={{ fontFamily: "'Playfair Display', serif", color: "#3e2723" }}
      >
        ✨ You’re Invited!
      </Typography>

      <Paper
        sx={{
          p: 4,
          background: bgColor,
          borderRadius: 6,
          boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
          border: "1px solid #d7ccc8",
          backdropFilter: "blur(3px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt="Flyover thumbnail"
            sx={{ width: "100%", maxHeight: 300, borderRadius: 3, objectFit: "cover", mb: 3 }}
          />
        )}

        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ fontFamily: "'Playfair Display', serif", color: "#4e342e", mb: 1 }}
        >
          {title}
        </Typography>

        <Typography
          variant="body1"
          fontStyle="italic"
          sx={{ fontSize: "1.05rem", color: "#5d4037", mb: 2, textAlign: "center" }}
        >
          {description}
        </Typography>

        <Stack spacing={1} alignItems="center" sx={{ mb: 3 }}>
          {talentName && (
            <Typography variant="body2" fontWeight={500}>🎙️ Talent: {talentName}</Typography>
          )}
          {suitType && (
            <Typography variant="body2" fontWeight={500}>🎭 Suit: {suitType}</Typography>
          )}
          {price && (
            <Typography variant="body2" fontWeight={500}>💰 Price: {price}</Typography>
          )}
          {members !== null && (
            <Typography variant="body2" fontWeight={500}>👥 Max Members: {members}</Typography>
          )}
          {duration && (
            <Typography variant="body2" fontWeight={500}>⏱️ Duration: {duration}</Typography>
          )}
          {dateTime && (
            <Typography variant="body2" fontWeight={500}>🕒 Time & Date: {new Date(dateTime).toLocaleString()}</Typography>
          )}
        </Stack>

        {gallery.length > 0 && (
          <Box sx={{ width: "100%", mb: 3 }}>
            <Slider {...sliderSettings}>
              {gallery.map((img, index) => (
                <Box
                  key={index}
                  component="img"
                  src={img}
                  alt={`Gallery image ${index + 1}`}
                  onClick={() => setFullscreenImg(img)}
                  sx={{
                    width: "100%",
                    height: 250,
                    objectFit: "cover",
                    borderRadius: 3,
                    cursor: "pointer",
                  }}
                />
              ))}
            </Slider>
          </Box>
        )}

        <QRCodeCanvas value={previewUrl} style={{ marginTop: 12 }} />
        <Typography variant="caption" mt={2} display="block" sx={{ color: "#6d4c41" }}>
          {previewUrl}
        </Typography>
      </Paper>

      <Dialog open={!!fullscreenImg} onClose={() => setFullscreenImg(null)} maxWidth="lg">
        <DialogContent sx={{ p: 0 }}>
          <Box
            component="img"
            src={fullscreenImg || ""}
            alt="Fullscreen preview"
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CreateFlyoverPage;
